/* eslint-disable max-lines */
import { useSpecialtySearch } from '@/components/ui/select/clinician-type-select/useSpecialtySearch';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useUserCountryCode } from '@/hooks/query/useUserCountryCode';
import { cn } from '@/lib/cn';
import { type ClinicianTypeInput } from '@/lib/types';
import { ComboboxTrigger } from '@heidi/design-system/combobox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@heidi/design-system/command';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyP2 } from '@heidi/design-system/typography';
import {
  ClinicianOtherInput,
  isClinicianSelectShortcutsDisabledAtom,
} from '@heidi/growth';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys, useRecordHotkeys } from 'react-hotkeys-hook';
import { FormattedMessage, useIntl } from 'react-intl';
import { Key } from 'ts-key-enum';
import { SpecialtiesLoadError } from './specialties-load-error';
import {
  OTHER_SPECIALTY_ID,
  OTHER_SPECIALTY_NAME,
  useSelectedSpecialtyDisplayName,
} from './useSelectedSpecialtyDisplayName';
export { OTHER_SPECIALTY_ID, OTHER_SPECIALTY_NAME };

type Props = {
  selectedSpecialty?: { id: string; name: string; group: { id: string; name: string } };
  setSelectedSpecialty: (specialty: ClinicianTypeInput) => void;
  otherSpecialty?: string;
  setOtherSpecialty: (value?: string) => void;
  settingsLayout?: boolean;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  /** In-form country override for pre-persist flows (signup/onboarding). When
   *  provided, takes precedence over the logged-in user's persisted country. */
  countryCode?: string;
  /** Id of the trigger, for a `<Label htmlFor>`. */
  id?: string;
};

const ClinicianTypeSelect = ({
  selectedSpecialty,
  setSelectedSpecialty,
  otherSpecialty,
  setOtherSpecialty,
  settingsLayout,
  className,
  disabled,
  placeholder,
  countryCode,
  id,
}: Props) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isKeyboardMode, setIsKeyboardMode] = useState(true);
  const [isShortcutsDisabled] = useAtom(isClinicianSelectShortcutsDisabledAtom);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [, { stop }] = useRecordHotkeys();

  const userCountryCode = useUserCountryCode();
  const country = countryCode ?? userCountryCode;
  const {
    isPending,
    isError,
    error,
    data: specialties,
  } = useQuerySpecialties((data) => data.specialties, { country });
  const { search: searchSpecialties, isReady } = useSpecialtySearch(specialties);

  const selectedSpecialtyDisplayName = useSelectedSpecialtyDisplayName(
    selectedSpecialty,
    specialties
  );

  const filteredSpecialties = useMemo(() => {
    if (!search || !isReady) return specialties || [];
    return searchSpecialties(search);
  }, [search, searchSpecialties, specialties, isReady]);

  const displaySpecialties = useMemo(() => {
    // If user has typed something and no matches found, show an "Other" option
    if (filteredSpecialties.length === 0 && search.trim()) {
      return [
        {
          id: OTHER_SPECIALTY_ID,
          name: search.trim(),
          localizedName: search.trim(),
          group: {
            id: 'other',
            name: 'Other',
          },
        },
      ];
    }

    return filteredSpecialties;
  }, [filteredSpecialties, search]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [search, open]);

  useEffect(() => {
    if (open && itemRefs.current?.at(highlightedIndex) && isKeyboardMode) {
      itemRefs.current.at(highlightedIndex)?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex, open, isKeyboardMode]);

  const onSpecialtySelected = useCallback(
    (specialtyId: string) => {
      const specialty = displaySpecialties.find((spec) => spec.id === specialtyId) || {
        id: OTHER_SPECIALTY_ID,
        name: OTHER_SPECIALTY_NAME,
        group: { id: 'other', name: 'Other' },
      };

      if (specialty.id === OTHER_SPECIALTY_ID && specialty.group.id === 'other') {
        // User selected the dynamic "Other" option - set the typed text as otherSpecialty
        setSelectedSpecialty({
          id: OTHER_SPECIALTY_ID,
          name: OTHER_SPECIALTY_NAME,
          group: { id: 'other', name: 'Other' },
        });
        setOtherSpecialty(specialty.name);
      } else {
        setSelectedSpecialty(specialty);
        setOtherSpecialty(undefined);
      }

      setOpen(false);
      setSearch('');
      stop();
    },
    [displaySpecialties, setOtherSpecialty, setSelectedSpecialty, stop]
  );

  useHotkeys(
    Key.ArrowDown,
    (e) => {
      if (!open || isShortcutsDisabled) return;
      e.preventDefault();
      setIsKeyboardMode(true);
      setHighlightedIndex((prev) =>
        prev < displaySpecialties.length - 1 ? prev + 1 : prev
      );
    },
    {
      enableOnFormTags: true,
      enabled: !isShortcutsDisabled,
    },
    [open, isShortcutsDisabled, displaySpecialties.length]
  );

  useHotkeys(
    Key.ArrowUp,
    (e) => {
      if (!open || isShortcutsDisabled) return;
      e.preventDefault();
      setIsKeyboardMode(true);
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    },
    {
      enableOnFormTags: true,
      enabled: !isShortcutsDisabled,
    },
    [open, isShortcutsDisabled]
  );

  useHotkeys(
    Key.Enter,
    (e) => {
      if (!open || isShortcutsDisabled) return;
      e.preventDefault();
      if (displaySpecialties[highlightedIndex]) {
        onSpecialtySelected(displaySpecialties[highlightedIndex].id);
      }
    },
    {
      enableOnFormTags: true,
      enabled: !isShortcutsDisabled,
    },
    [open, isShortcutsDisabled, displaySpecialties, highlightedIndex, onSpecialtySelected]
  );

  useHotkeys(
    Key.Escape,
    () => {
      if (open) {
        setOpen(false);
        stop();
      }
      if (
        document.activeElement?.tagName === 'INPUT' &&
        document.activeElement &&
        'blur' in document.activeElement
      ) {
        (document.activeElement as HTMLElement).blur();
      }
    },
    {
      enableOnFormTags: ['INPUT', 'TEXTAREA'],
      enabled: !isShortcutsDisabled,
    },
    [open, stop, isShortcutsDisabled]
  );

  const handleMouseMove = () => {
    setIsKeyboardMode(false);
  };

  return (
    <div
      className={cn(
        'flex w-full',
        className,
        settingsLayout ? 'flex-row gap-4' : 'flex-col gap-3'
      )}
    >
      <div className="w-full">
        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <PopoverTrigger asChild>
            <ComboboxTrigger
              id={id}
              open={open}
              disabled={disabled}
              isPlaceholder={!selectedSpecialty}
              role="combobox"
              onKeyDown={(e) => {
                if (e.key === 'Tab' && open) {
                  setOpen(false);
                  return;
                }
                if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                  e.preventDefault();
                  setOpen(true);
                }
              }}
            >
              {selectedSpecialty ? (
                <TypographyP2 className={'truncate text-left'}>
                  {selectedSpecialtyDisplayName}
                </TypographyP2>
              ) : (
                <TypographyP2>
                  {placeholder ?? (
                    <FormattedMessage
                      defaultMessage="Select a clinician type"
                      id="Obl1fTvT9v"
                    />
                  )}
                </TypographyP2>
              )}
            </ComboboxTrigger>
          </PopoverTrigger>
          <PopoverContent
            matchTriggerWidth
            className="max-h-[--radix-popover-content-available-height] overflow-hidden p-0"
            sideOffset={4}
            align="center"
            updatePositionStrategy="optimized"
            side="bottom"
            onKeyDownCapture={(e) => {
              if (e.key === 'Tab') {
                setOpen(false);
              }
            }}
          >
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={intl.formatMessage({
                  defaultMessage: 'Search clinician types...',
                  id: '4DxUWfRSkA',
                })}
                onValueChange={setSearch}
              />

              {isPending ? (
                <div className="p-1">
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : null}
              {isError ? (
                <SpecialtiesLoadError error={error} className="p-3" />
              ) : (
                <CommandEmpty>
                  <FormattedMessage
                    defaultMessage="No specialty found."
                    id="F9a9k/mnAM"
                  />
                </CommandEmpty>
              )}
              <ScrollArea
                className="max-h-[220px] overflow-scroll sm:overflow-auto"
                viewportClassName="[&>div]:!min-w-0 [&>div]:!block"
              >
                <CommandGroup onMouseMove={handleMouseMove}>
                  {displaySpecialties.map((specialty, index) => (
                    <CommandItem
                      ref={(el: HTMLDivElement | null) => {
                        itemRefs.current[index] = el;
                      }}
                      className={cn(
                        'flex w-full min-w-0 items-center justify-between gap-3',
                        isKeyboardMode
                          ? [
                              'data-[selected=true]:bg-transparent',
                              index === highlightedIndex && 'bg-background-secondary',
                            ]
                          : ['hover:bg-background-secondary']
                      )}
                      key={specialty.id}
                      value={specialty.id}
                      onSelect={() => onSpecialtySelected(specialty.id)}
                    >
                      <TypographyP2 className={'truncate text-left'}>
                        {specialty.localizedName}
                      </TypographyP2>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      {(selectedSpecialty?.id.startsWith(OTHER_SPECIALTY_ID) ||
        // Legacy fallback: some callers pass a backend specialty literally named
        // "Other" under its own id. The comparison is against the canonical
        // (English) name slot, so it is locale-independent by design.
        selectedSpecialty?.name === OTHER_SPECIALTY_NAME) &&
        (settingsLayout ? (
          <div className="w-full">
            <ClinicianOtherInput
              className={'h-full'}
              wrapperClassName={'h-full'}
              otherSpecialty={otherSpecialty}
              setOtherSpecialty={setOtherSpecialty}
            />
          </div>
        ) : (
          <div className="w-full">
            <ClinicianOtherInput
              className={'h-full min-h-[34px]'}
              wrapperClassName={'h-full min-h-[34px]'}
              otherSpecialty={otherSpecialty}
              setOtherSpecialty={setOtherSpecialty}
            />
          </div>
        ))}
    </div>
  );
};

export default ClinicianTypeSelect;
